/* InsulinConceptsModal — патент-friendly обяснение на NIS / DIL / VAIS.
 *
 * Решава честа UX-confusion: пациент вижда "NIS 50-60%" и си мисли че
 * инсулинът му е висок. Реалността: NIS е оценка на ПОТЕНЦИАЛЕН инсулиногенен
 * ефект на храната — НЕ реално измерване на кръвен инсулин.
 *
 * Тригер: <InfoTooltip /> бутон до всяка NIS/DIL стойност + меню "Помощ".
 */
import React from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { X, Activity, Flame, TrendingDown, AlertCircle, CheckCircle2, BookOpen } from "lucide-react";
import { Link } from "react-router-dom";

export default function InsulinConceptsModal({ open, onClose, focus = "all" }) {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  if (!open) return null;

  const sections = [
    {
      id: "nis",
      icon: Activity,
      color: "from-violet-500 to-fuchsia-500",
      title_bg: "NIS — Инсулинов индекс на хранителния избор (0-100%)",
      title_en: "NIS — Nutramilo Insulin Score (0-100%)",
      body_bg: [
        "Какво показва: колко силно ОПРЕДЕЛЕНА ХРАНА теоретично стимулира инсулина. Това е оценка на самата храна — не на твоето тяло.",
        "Изчислява се по: протеин, въглехидрати, мазнини, фибри + типа хранителен източник (растителен/животински/смесен). Базирано на peer-reviewed Food Insulin Index (Holt-Bell-Bao n=147 кохорта).",
        "ВАЖНО: NIS 50-60% НЕ значи че инсулинът ти е висок. Това е оценка на ПОТЕНЦИАЛА на храната. Дали реално покачваш инсулин зависи от твоята инсулинова чувствителност, телесен състав, сън, стрес и моментно състояние.",
        "Пример: метаболитно здрав човек може да яде ежедневно средно 55% NIS храна без покачен фасциращ инсулин. Човек с инсулинова резистентност при същия NIS може да има скочил инсулин — защото проблемът е в чувствителността, не в храната сама по себе си.",
      ],
      body_en: [
        "What it shows: how strongly a SPECIFIC FOOD theoretically stimulates insulin. This rates the food itself — not your body.",
        "Computed from: protein, carbs, fat, fiber + source type (plant/animal/mixed). Based on peer-reviewed Food Insulin Index (Holt-Bell-Bao n=147 cohort).",
        "IMPORTANT: NIS 50-60% does NOT mean your insulin is high. It rates the food's POTENTIAL. Whether you actually spike insulin depends on your insulin sensitivity, body composition, sleep, stress, and momentary state.",
        "Example: a metabolically healthy person can eat 55% average NIS daily without elevated fasting insulin. Someone with insulin resistance may spike on the same NIS — because the problem is sensitivity, not the food itself.",
      ],
    },
    {
      id: "dil",
      icon: Flame,
      color: "from-amber-500 to-orange-500",
      title_bg: "DIL — Диетично инсулиново натоварване (грамове)",
      title_en: "DIL — Dietary Insulin Load (grams)",
      body_bg: [
        "Какво показва: кумулативно дневно натоварване в грамове — колко общо инсулиногенен потенциал си консумирал.",
        "Формула: 0.69 × протеин (g) + max(въглехидрати − фибри, 0). Това е независимо регресирана версия на класическия FII (peer-reviewed).",
        "Защо е по-полезно от NIS%: NIS показва качество на едно хранене, DIL показва КОЛИЧЕСТВО за деня. 100g риба (NIS 33%) дава DIL ≈ 20g; 100g бял ориз (NIS 80%) дава DIL ≈ 28g.",
        "Дневният ти DIL target се персонализира по тегло, цел (отслабване / поддръжка / мускулна маса) и (за жени) фаза на цикъла. Виж го с InfoTooltip до твоя DIL.",
      ],
      body_en: [
        "What it shows: cumulative daily load in grams — your total insulinogenic potential consumed.",
        "Formula: 0.69 × protein (g) + max(carbs − fiber, 0). Independently regressed version of the classic FII (peer-reviewed).",
        "Why it's more useful than NIS%: NIS shows quality of one meal, DIL shows DAILY QUANTITY. 100g fish (NIS 33%) gives DIL ≈ 20g; 100g white rice (NIS 80%) gives DIL ≈ 28g.",
        "Your daily DIL target is personalized to weight, goal (loss / maintain / muscle gain) and (for women) cycle phase. See yours via the InfoTooltip next to your DIL.",
      ],
    },
    {
      id: "vais",
      icon: TrendingDown,
      color: "from-rose-500 to-pink-500",
      title_bg: "VAIS — Висцерален мастен индекс",
      title_en: "VAIS — Visceral Adiposity Index",
      body_bg: [
        "Какво показва: оценка на ВЪТРЕШНАТА (висцерална) мазнина около органите — най-опасният тип мазнина.",
        "Защо е важно: висцералната мазнина е тясно свързана с инсулинова резистентност, сърдечно-съдов риск, неалкохолна мастна болест на черния дроб (NAFLD) и метаболитен синдром. Подкожната мазнина (под кожата) е значително по-малко опасна.",
        "Как се изчислява: от обиколка на талия, тегло, височина, пол и възраст (валидиран модел Amato 2010). НЕ изисква скенер — само сантиметър и кантар.",
        "Полза от следене: ако намаляваш VAIS дори с 10-15% за 3 месеца, понижаваш риска от диабет тип 2 със ~40% (epidemiological data, Wang 2014).",
        "Един от малкото метаболитни маркери, които можеш активно да промениш чрез храна, движение и сън. Не е генетично фиксиран.",
      ],
      body_en: [
        "What it shows: estimate of INTERNAL (visceral) fat around organs — the most dangerous type of fat.",
        "Why it matters: visceral fat is strongly linked to insulin resistance, cardiovascular risk, NAFLD and metabolic syndrome. Subcutaneous fat (under the skin) is significantly less dangerous.",
        "How it's calculated: from waist, weight, height, sex and age (validated Amato 2010 model). NO scanner needed — just a tape measure and scale.",
        "Benefit of tracking: reducing VAIS by 10-15% over 3 months lowers type 2 diabetes risk ~40% (epidemiological data, Wang 2014).",
        "One of the few metabolic markers you can actively change through food, movement and sleep. Not genetically fixed.",
      ],
    },
  ];

  const filtered = focus === "all" ? sections : sections.filter((s) => s.id === focus);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 bg-black/60 backdrop-blur-sm"
         data-testid="insulin-concepts-modal" onClick={onClose}>
      <div className={`relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl ${
              isDark ? "bg-[hsl(223,47%,9%)] border border-white/10" : "bg-white border border-slate-200"
            }`}
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={`sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b ${
              isDark ? "bg-[hsl(223,47%,9%)] border-white/10" : "bg-white border-slate-200"
            }`}>
          <div>
            <h2 className={`text-lg font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              {bg ? "Какво всъщност показват NIS, DIL и VAIS?" : "What do NIS, DIL and VAIS actually mean?"}
            </h2>
            <p className={`text-[11px] mt-0.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {bg ? "Кратко обяснение на човешки език — без жаргон." : "A short, plain-language explanation — no jargon."}
            </p>
          </div>
          <button onClick={onClose} data-testid="insulin-concepts-close"
                  className={`p-2 rounded-lg transition-colors ${
                    isDark ? "hover:bg-white/10 text-slate-400" : "hover:bg-slate-100 text-slate-600"
                  }`}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          {/* Critical disclaimer */}
          <div className={`flex items-start gap-3 p-3.5 rounded-xl border ${
                isDark ? "border-amber-500/30 bg-amber-500/5" : "border-amber-200 bg-amber-50"
              }`}>
            <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isDark ? "text-amber-300" : "text-amber-700"}`} />
            <div className={`text-[12px] leading-relaxed ${isDark ? "text-amber-100" : "text-amber-900"}`}>
              <strong>{bg ? "Важно за разбирането:" : "Important to understand:"}</strong>{" "}
              {bg
                ? "Това са оценки на ХРАНАТА и НАВИЦИТЕ — не директни измервания на кръвен инсулин или висцерална мазнина чрез скенер. Реалният отговор на тялото ти зависи от твоята чувствителност, активност, сън и геника. Затова данните ти за 30 дни казват повече от един ден."
                : "These are estimates of FOOD and HABITS — not direct measurements of blood insulin or visceral fat via scanner. Your body's actual response depends on your sensitivity, activity, sleep and genetics. That's why 30 days of your data say more than any single day."}
            </div>
          </div>

          {/* Sections */}
          {filtered.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.id} className={`rounded-xl border overflow-hidden ${
                    isDark ? "border-white/10 bg-white/[0.02]" : "border-slate-200 bg-slate-50/50"
                  }`} data-testid={`insulin-concept-${s.id}`}>
                <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200/60 dark:border-white/5">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center bg-gradient-to-br ${s.color} text-white`}>
                    <Icon className="w-4.5 h-4.5" />
                  </div>
                  <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                    {bg ? s.title_bg : s.title_en}
                  </h3>
                </div>
                <div className="p-4 space-y-2.5">
                  {(bg ? s.body_bg : s.body_en).map((para, i) => (
                    <p key={i} className={`text-[12.5px] leading-relaxed ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                      {para}
                    </p>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Bottom CTA */}
          <div className={`flex items-start gap-3 p-3.5 rounded-xl border ${
                isDark ? "border-emerald-500/30 bg-emerald-500/5" : "border-emerald-200 bg-emerald-50"
              }`}>
            <CheckCircle2 className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isDark ? "text-emerald-300" : "text-emerald-700"}`} />
            <div className={`text-[12px] leading-relaxed ${isDark ? "text-emerald-100" : "text-emerald-900"}`}>
              <strong>{bg ? "Какво означава за теб?" : "What does this mean for you?"}</strong>{" "}
              {bg
                ? "Не се страхувай от единичен 60% NIS обяд — гледай тенденцията за седмицата и дали DIL стои в твоя дневен target. Здраво метаболизмо тяло може да понесе много по-широка ивица отколкото мислиш."
                : "Don't fear a single 60% NIS meal — watch the weekly trend and whether DIL stays within your daily target. A metabolically healthy body tolerates a much wider band than you think."}
              <div className="mt-2 flex flex-wrap gap-2">
                <Link to="/scientific-formulas" data-testid="insulin-concepts-science-link"
                      className={`inline-flex items-center gap-1 text-[11px] font-bold ${
                        isDark ? "text-emerald-300 hover:text-emerald-200" : "text-emerald-700 hover:text-emerald-800"
                      } hover:underline`}>
                  <BookOpen className="w-3 h-3" />
                  {bg ? "Прочети peer-reviewed методологията" : "Read the peer-reviewed methodology"}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
